import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

import { Member, MemberSectionLink } from 'src/app/classes/member';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Environment } from '../classes/environment';
import { AuthenticationService } from './authentication.service';
import { Section } from '../classes/section';



@Injectable( { providedIn: 'root' } )
export class MemberService {


	constructor(
        private _http: HttpClient,
		@Inject('environment') private environment: Environment,
		private matSnackBar: MatSnackBar,
		private authenticationService: AuthenticationService
	) {}


	getAll(): Observable<Member[]> {

		return this._http.get<Member[]>( 
			this.environment.backendUrl + 'mitglied/all', 
		).pipe( 
			map( members => {
				// console.log( 'MEMBER.SERVICE members=', members )
				if( !members ) members = [];
				for( const m of members ) {
					if( !m.mitgliedsname ) m.mitgliedsname = '';
				}			
				members.sort( (a,b) => 
					a.mitgliedsname.toLocaleLowerCase().localeCompare( 
						b.mitgliedsname.toLocaleLowerCase() 
					)
				);
				return members;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the list of members', err );
				this.matSnackBar.open( 'Beim Abrufen der Mitgliederliste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	getAllExceptPrivate(): Observable<Member[]> {

		if( this.authenticationService.isAdmin() ) return this.getAll();

		return forkJoin( {
			my: this.getMyMember(),
			all: this.getAll()
		}).pipe(
			map( fork => {
				return fork.all.filter( m => (!m.privateDaten || m.mitgliedId == fork.my.mitgliedid) )
			})
		);

	}


	getMyMember(): Observable<Member> {

		const user = this.authenticationService.getCurrentUser();

		return this._http.get<Member>( 
			this.environment.backendUrl + 'mitglied/benutzer/' + user.ebeId, 
		).pipe( 
			map( (member: Member) => {
				// console.log( 'getMyMember', member )
				if( !member || !member.mitgliedId ) return new Member();
				return member;
			}),
			catchError(err => {
				console.log( 'An error occurred while fetching the list of members', err );
				this.matSnackBar.open( 'Beim Abrufen der Mitgliederliste ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( new Member() );
			})
		);

	}

	getManageableMembers(): Observable<Member[]> {

		return ( this.authenticationService.isAdmin() )
			? this.getAll()
			: this.getMyMember().pipe( map(member => [member]) );

	}

	saveMember( member: Member ): Observable<Member | null> {

		return member.mitgliedId ? this.updateMember( member ) : this.createMember( member );

	}

	private createMember( member: Member ): Observable<Member | null> {

		return this._http.post<{ verarbeitet: boolean, mitglied: Member }>( 
			this.environment.backendUrl + 'mitglied/new', 
			member
		).pipe( 
			map( response => {
				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Erstellung fehlgeschlagen', '', { duration: 5000 } );
					return null;
				}
				this.matSnackBar.open( 'Erstellung gespeichert', '', { duration: 5000 } );
				return response.mitglied;
			}),
			catchError(err => {
				console.log( 'An error occurred while creating the member', err );
				this.matSnackBar.open( 'Beim Erstellen des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(null);
			})
		);
	}


	private updateMember( member: Member ): Observable<Member | null> {

		return this._http.put<{ verarbeitet: boolean, mitglied: Member }>( 
			this.environment.backendUrl + 'mitglied/update', 
			member
		).pipe( 
			map( response => {
				if( !response || !response.verarbeitet ) {
					this.matSnackBar.open( 'Änderung fehlgeschlagen', '', { duration: 5000 } );
					return null;
				}
				this.matSnackBar.open( 'Änderung gespeichert', '', { duration: 5000 } );
				return response.mitglied;
			}),
			catchError(err => {
				console.log( 'An error occurred while updating the member', err );
				this.matSnackBar.open( 'Beim Aktualisieren des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of(null);
			})
		);
	}


	getSectionsOfMember( memberId: string ): Observable<MemberSectionLink[]> {

		return this._http.get<MemberSectionLink[]>(
			this.environment.backendUrl + 'ms/mitglied/' + memberId
		).pipe(
			map( msLinks => {
				if( !msLinks ) return [];
				msLinks.sort( (a,b) => 
					a.sparteId.toLocaleLowerCase().localeCompare(
						b.sparteId.toLocaleLowerCase()
					)
				);
				return msLinks;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the sections of the member', err );
				this.matSnackBar.open( 'Beim Abrufen der Sparte des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	getHistoricalSectionsOfMembers( memberIds: string[] ): Observable<Section[]> {

		const params = new HttpParams()
			.set( 'mitgliedIds', memberIds.toString() );

		return this._http.get<Section[]>(
			this.environment.backendUrl + 'sparte/historic',
			{ params: params }
		).pipe(
			map( sections => {
				if( !sections ) sections = [];
				sections.sort( (a,b) => 
					a.sparteBezeichnung.toLocaleLowerCase().localeCompare(
						b.sparteBezeichnung.toLocaleLowerCase()
					)
				)
				return sections;
			}),
			catchError( err => {
				console.log( 'An error occurred while fetching the sections of the member', err );
				this.matSnackBar.open( 'Beim Abrufen der Sparte des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		)
	}

	createMemberSectionLink( msLink: MemberSectionLink ): Observable<MemberSectionLink> {

		return this._http.post<{ verarbeitet: boolean, ms: MemberSectionLink }>(
			this.environment.backendUrl + 'ms/new',
			msLink
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'An error occurred while creating the link between the section and the member' );
					this.matSnackBar.open( 'Beim Erstellen der Verbindung zwischen dem Abschnitt und dem Mitglied ist ein Fehler aufgetreten', '', { duration: 5000 } );
					return new MemberSectionLink();
				}
				this.matSnackBar.open( 'Verbindung erfolgreich', '', { duration: 5000 } );
				return response.ms;
			}),
			catchError( err => {
				console.log( 'An error occurred while creating the link between the section and the member', err );
				this.matSnackBar.open( 'Beim Erstellen der Verbindung zwischen dem Abschnitt und dem Mitglied ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of( new MemberSectionLink() );
			})
		);

	}

	updateMemberSectionLinks( msLinks: MemberSectionLink[] ): Observable<MemberSectionLink[]> {

		return this._http.put<{ verarbeitet: boolean, mss: MemberSectionLink[] }>(
			this.environment.backendUrl + 'ms/update/batch',
			msLinks
		).pipe(
			map( response => {
				if( !response || !response.verarbeitet ) {
					console.log( 'An error occurred while updating the sections links of the member', response );
					this.matSnackBar.open( 'Beim Aktualisieren der Abschnittslinks des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
					return [];
				}

				this.matSnackBar.open( 'Aktualisierung der Abschnittslinks des Mitglieds erfolgreich', '', { duration: 5000 } );
				return response.mss;
			}),
			catchError( err => {
				console.log( 'An error occurred while updating the sections links of the member', err );
				this.matSnackBar.open( 'Beim Aktualisieren der Abschnittslinks des Mitglieds ist ein Fehler aufgetreten', '', { duration: 5000 } );
				return of([]);
			})
		);

	}

	setMitgliedMainContact( ebeId: string, mitgliedId: string, b=true ): Observable<boolean> {

		return this._http.put<{ verarbeitet: boolean }>( 
			this.environment.backendUrl + 'bm/hauptansprechpartner', 
			{
				ebeId: ebeId,
				mitgliedId: mitgliedId,
				hauptansprechpartner: b
			}
		).pipe( 

			map( answer => {
				return !!(answer && answer.verarbeitet);
			}),
			catchError( err => {
				console.log( 'An error occurred while setting the main contact', err );
				this.matSnackBar.open( 'Beim Festlegen des Hauptansprechpartners ist ein Fehler aufgetreten.', '', { duration: 5000 } );
				return of(false);
			})
			
		);

	}

}